// Comparação Múltipla de Cenários CLT × PJ (Júnior/Pleno/Sênior etc.)
// Chama o mesmo endpoint /api/clt-pj/simulate N vezes e renderiza um PDF
// executivo com todos os cenários lado a lado.
import { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Users, Printer, Image as ImageIcon, Save, Mail, X, Plus, Trash2, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { saveAs } from "file-saver";

const BRL = (v) => `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PCT = (v) => `${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

function todayStamp() {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}-${String(d.getHours()).padStart(2, "0")}${String(d.getMinutes()).padStart(2, "0")}`;
}

const DEFAULT_SCENARIOS = [
  { name: "Júnior", salario_clt: 3500, valor_pj: 4900 },
  { name: "Pleno",  salario_clt: 7000, valor_pj: 9800 },
  { name: "Sênior", salario_clt: 15000, valor_pj: 21000 },
];

export default function MultiScenarioDialog({ open, onClose, baseInputs, meta }) {
  const reportRef = useRef(null);
  const [scenarios, setScenarios] = useState(() =>
    DEFAULT_SCENARIOS.map((s) => ({ ...s, result: null, loading: false, error: null }))
  );
  const [exporting, setExporting] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [destinatarios, setDestinatarios] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSending, setEmailSending] = useState(false);

  const allReady = useMemo(
    () => scenarios.length > 0 && scenarios.every((s) => s.result && !s.loading && !s.error),
    [scenarios]
  );

  if (!open) return null;

  const setScenario = (i, patch) =>
    setScenarios((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const addScenario = () => {
    if (scenarios.length >= 5) { toast.error("Máximo 5 cenários no mesmo relatório."); return; }
    setScenarios((prev) => [...prev, { name: `Cenário ${prev.length + 1}`, salario_clt: 5000, valor_pj: 7000, result: null, loading: false, error: null }]);
  };

  const removeScenario = (i) => setScenarios((prev) => prev.filter((_, idx) => idx !== i));

  const runAll = async () => {
    const base = baseInputs || { clt: {}, pj: {} };
    const next = await Promise.all(scenarios.map(async (s) => {
      try {
        const body = {
          clt: {
            ...base.clt,
            salario_base: Number(s.salario_clt),
          },
          pj: {
            ...base.pj,
            valor_mensal: Number(s.valor_pj),
          },
          competencia_ano: meta?.competenciaAno || new Date().getFullYear(),
        };
        const { data } = await api.post("/clt-pj/simulate", body);
        return { ...s, result: data, loading: false, error: null, inputs: body };
      } catch (e) {
        return { ...s, result: null, loading: false, error: formatApiError(e.response?.data?.detail) };
      }
    }));
    setScenarios(next);
    const okCount = next.filter((n) => n.result).length;
    if (okCount === next.length) toast.success(`${okCount} cenário(s) calculado(s).`);
    else toast.warning(`${okCount}/${next.length} cenários OK — veja mensagens abaixo.`);
  };

  const renderPdfBlob = async () => {
    const canvas = await html2canvas(reportRef.current, {
      scale: 2.4, backgroundColor: "#ffffff", useCORS: true, logging: false,
    });
    const pdf = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgW = pageW - 10;
    const imgH = (canvas.height * imgW) / canvas.width;
    if (imgH <= pageH - 10) {
      pdf.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", 5, 5, imgW, imgH);
    } else {
      const pxPerMm = canvas.width / imgW;
      const sliceHeightPx = Math.floor((pageH - 10) * pxPerMm);
      let y = 0;
      while (y < canvas.height) {
        const h = Math.min(sliceHeightPx, canvas.height - y);
        const slice = document.createElement("canvas");
        slice.width = canvas.width; slice.height = h;
        slice.getContext("2d").drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
        if (y > 0) pdf.addPage("a4", "landscape");
        pdf.addImage(slice.toDataURL("image/jpeg", 0.92), "JPEG", 5, 5, imgW, h / pxPerMm);
        y += h;
      }
    }
    const blob = pdf.output("blob");
    const base64 = (pdf.output("datauristring").split(",")[1] || "").trim();
    return { blob, base64 };
  };

  const exportPdf = async () => {
    setExporting("pdf");
    try {
      const { blob } = await renderPdfBlob();
      saveAs(blob, `relatorio-multi-cenarios-${todayStamp()}.pdf`);
      toast.success("PDF multi-cenário gerado.");
    } catch (e) { toast.error("Falha PDF: " + e.message); }
    finally { setExporting(null); }
  };

  const exportPng = async () => {
    setExporting("png");
    try {
      const canvas = await html2canvas(reportRef.current, {
        scale: 3, backgroundColor: "#ffffff", useCORS: true, logging: false,
      });
      canvas.toBlob((blob) => {
        if (blob) saveAs(blob, `relatorio-multi-cenarios-${todayStamp()}.png`);
        toast.success("PNG alta resolução gerado.");
      }, "image/png");
    } catch (e) { toast.error("Falha PNG: " + e.message); }
    finally { setExporting(null); }
  };

  const saveToHistory = async () => {
    try {
      const body = {
        company_id: meta?.companyId || null,
        title: `Multi-Cenário · ${scenarios.map(s => s.name).join(" / ")}`,
        scenario_type: "multi",
        inputs: baseInputs,
        result: {},  // o result por cenário fica em `scenarios`
        meta,
        scenarios: scenarios.map((s) => ({ name: s.name, inputs: s.inputs, result: s.result })),
      };
      const { data } = await api.post("/clt-pj/reports", body);
      toast.success(`Relatório multi-cenário salvo (${data.id.slice(0, 8)}…).`);
      return data.id;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return null; }
  };

  const sendEmail = async () => {
    const emails = destinatarios.split(/[,;\s]+/).map(e => e.trim()).filter(e => e && e.includes("@"));
    if (!emails.length) { toast.error("Informe ao menos um e-mail válido."); return; }
    setEmailSending(true);
    try {
      const { base64 } = await renderPdfBlob();
      const rid = await saveToHistory();
      const { data: resp } = await api.post("/clt-pj/reports/send-email", {
        destinatarios: emails, subject: `Multi-Cenário CLT × PJ · Diretoria`,
        message: emailMessage || null, pdf_base64: base64,
        filename: `multi-cenarios-${todayStamp()}.pdf`, report_id: rid,
      });
      if (resp.status === "sent") toast.success(`E-mail enviado para ${emails.length} destinatário(s).`);
      else if (resp.status === "skipped_no_resend_key") toast.warning("RESEND_API_KEY vazia — envio registrado mas e-mail não saiu.", { duration: 8000 });
      setEmailOpen(false);
    } catch (e) { toast.error(e.message); }
    finally { setEmailSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose && onClose()}>
      <DialogContent className="max-w-[1240px] max-h-[94vh] overflow-hidden p-0 bg-white">
        <DialogHeader className="px-5 pt-4 pb-2 border-b border-slate-200">
          <DialogTitle className="font-display text-[18px] text-[#234935]">
            Comparação Múltipla · Cenários CLT × PJ
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Configure até 5 cenários (ex.: Júnior/Pleno/Sênior), rode o cálculo e gere um relatório único.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto bg-slate-100" style={{ maxHeight: "72vh" }}>
          {/* Config dos cenários */}
          <div className="px-4 pt-3 pb-3 bg-white border-b border-slate-200">
            <div className="flex flex-wrap gap-2 items-end mb-2">
              {scenarios.map((s, i) => (
                <div key={i} className="rounded-md border border-slate-300 p-2 flex gap-2 items-end bg-slate-50"
                     data-testid={`scenario-row-${i}`}>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Nome</Label>
                    <Input value={s.name} onChange={(e) => setScenario(i, { name: e.target.value })}
                           className="h-7 w-[110px] text-[11px]" data-testid={`sc-name-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Salário CLT</Label>
                    <Input type="number" value={s.salario_clt}
                           onChange={(e) => setScenario(i, { salario_clt: e.target.value })}
                           className="h-7 w-[110px] text-[11px] font-mono" data-testid={`sc-clt-${i}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] uppercase tracking-wider">Valor PJ (NF)</Label>
                    <Input type="number" value={s.valor_pj}
                           onChange={(e) => setScenario(i, { valor_pj: e.target.value })}
                           className="h-7 w-[110px] text-[11px] font-mono" data-testid={`sc-pj-${i}`} />
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeScenario(i)}
                          className="h-7 w-7 p-0 text-rose-500" data-testid={`sc-rm-${i}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <Button variant="outline" size="sm" onClick={addScenario} className="h-9" data-testid="sc-add">
                <Plus className="h-3.5 w-3.5 mr-1" /> Cenário
              </Button>
              <Button size="sm" onClick={runAll} className="h-9 bg-[#065F46] hover:bg-[#054d38] text-white"
                      data-testid="sc-run-all">
                <PlayCircle className="h-3.5 w-3.5 mr-1.5" /> Calcular todos
              </Button>
            </div>
            <div className="text-[11px] text-slate-500">
              Os parâmetros CLT/PJ fixos (RAT/FAP, Terceiros, Dependentes, Regime, ISS, Contador) são herdados da simulação da tela principal.
            </div>
          </div>

          {/* Relatório renderizável */}
          <div className="px-4 py-3">
            {!allReady ? (
              <div className="rounded-md bg-white border border-dashed border-slate-300 p-10 text-center text-slate-500 text-[12px]">
                Configure os cenários acima e clique em <b>Calcular todos</b>. O relatório será montado automaticamente.
              </div>
            ) : (
              <div ref={reportRef}>
                <MultiReportDocument scenarios={scenarios} meta={meta} />
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="px-5 py-3 border-t border-slate-200 bg-white flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} className="mr-auto" data-testid="btn-close-multi">
            <X className="h-3.5 w-3.5 mr-1" /> Fechar
          </Button>
          <Button variant="outline" onClick={saveToHistory} disabled={!allReady || !!exporting}
                  data-testid="btn-multi-save">
            <Save className="h-3.5 w-3.5 mr-1.5" /> Salvar no Histórico
          </Button>
          <Button variant="outline" onClick={() => setEmailOpen(true)} disabled={!allReady || !!exporting}
                  data-testid="btn-multi-email">
            <Mail className="h-3.5 w-3.5 mr-1.5" /> Enviar para Diretoria
          </Button>
          <Button variant="outline" onClick={exportPng} disabled={!allReady || !!exporting}
                  data-testid="btn-multi-png">
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> {exporting === "png" ? "Gerando..." : "PNG"}
          </Button>
          <Button onClick={exportPdf} disabled={!allReady || !!exporting}
                  className="bg-[#234935] hover:bg-[#2c5c43] text-white" data-testid="btn-multi-pdf">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> {exporting === "pdf" ? "Gerando..." : "PDF multi-cenário"}
          </Button>
        </DialogFooter>
      </DialogContent>

      {emailOpen && (
        <Dialog open onOpenChange={(v) => !v && setEmailOpen(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Enviar multi-cenário por e-mail</DialogTitle>
              <DialogDescription>Separe destinatários por vírgula, ponto-e-vírgula ou nova linha.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Destinatários *</Label>
                <Textarea rows={3} value={destinatarios} onChange={(e) => setDestinatarios(e.target.value)}
                          data-testid="multi-email-dests" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider">Mensagem (opcional)</Label>
                <Textarea rows={3} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)}
                          data-testid="multi-email-msg" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEmailOpen(false)} disabled={emailSending}>Cancelar</Button>
              <Button onClick={sendEmail} disabled={emailSending}
                      className="bg-[#234935] hover:bg-[#2c5c43] text-white"
                      data-testid="btn-multi-send-email">
                <Mail className="h-3.5 w-3.5 mr-1.5" /> {emailSending ? "Enviando..." : "Enviar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

// ============================================================================
// Documento visual multi-cenário
// ============================================================================
function MultiReportDocument({ scenarios, meta }) {
  const cols = scenarios.length;
  const cm = scenarios.map((s) => s.result?.comparativo || {});
  const bestEconomy = cm.reduce((best, c, i) =>
    (c.custo_empresa?.economia_pct || 0) > (best.v || -Infinity)
      ? { v: c.custo_empresa.economia_pct, i } : best,
    { v: -Infinity, i: -1 }
  );
  const now = new Date();

  return (
    <div style={{
      width: 1180,
      fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
      color: "#1a231f", background: "#ffffff", padding: 28,
      boxSizing: "border-box", fontSize: 11.5, lineHeight: 1.4,
    }}>
      {/* Header */}
      <div style={{ borderBottom: "3px solid #234935", paddingBottom: 12, marginBottom: 16, display: "flex", gap: 14, alignItems: "flex-start" }}>
        {meta?.logoUrl && (
          <img src={meta.logoUrl} alt="Logo" crossOrigin="anonymous"
               onError={(e) => { e.currentTarget.style.display = "none"; }}
               style={{ height: 60, maxWidth: 140, objectFit: "contain",
                        border: "1px solid #e5e7eb", borderRadius: 6, padding: 4, background: "#fff" }} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: "#8A6B2B", fontWeight: 700 }}>
            THEOKRATOS RH · ANÁLISE MULTI-CENÁRIO
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#234935", letterSpacing: 0.5 }}>
            COMPARAÇÃO MÚLTIPLA · {cols} CENÁRIOS
          </div>
          <div style={{ fontSize: 16, color: "#1a231f", fontWeight: 600 }}>
            CONTRATAÇÃO PJ × CONTRATAÇÃO CLT
          </div>
          <div style={{ display: "flex", gap: 16, marginTop: 8, fontSize: 10.5 }}>
            <MetaItem label="VIGÊNCIA" value={String(scenarios[0]?.result?.vigencia_tabelas || scenarios[0]?.result?.competencia_ano || now.getFullYear())} />
            {meta?.empresa && <MetaItem label="EMPRESA" value={meta.empresa} />}
            <MetaItem label="DATA" value={now.toLocaleDateString("pt-BR")} />
            {meta?.responsavel && <MetaItem label="RESPONSÁVEL" value={meta.responsavel} />}
          </div>
        </div>
      </div>

      {/* Grid de cenários */}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 12 }}>
        {scenarios.map((s, i) => {
          const r = s.result || {};
          const pjT = r.pj?.total_tributos || 0;
          const pjLq = r.pj?.liquido_colaborador_mensal || 0;
          const pjCe = r.pj?.custo_empresa_mensal || 0;
          const pjCeA = r.pj?.custo_empresa_anual || 0;
          const cltLq = r.clt?.liquido_folha_mensal || 0;
          const cltLqEco = r.clt?.liquido_provisionado_mensal || 0;
          const cltCe = r.clt?.custo_empresa_mensal || 0;
          const cltCeA = r.clt?.custo_empresa_anual || 0;
          const cmp = r.comparativo || {};
          const eco = cmp.custo_empresa?.economia_pct || 0;
          const highlight = i === bestEconomy.i;
          return (
            <div key={i} style={{
              border: highlight ? "3px solid #C5A059" : "2px solid #234935",
              borderRadius: 10, padding: 10, background: "#fff", position: "relative",
            }}>
              {highlight && (
                <div style={{ position: "absolute", top: -10, right: 10, background: "#C5A059",
                              color: "#1A231F", padding: "2px 8px", fontSize: 9, fontWeight: 700,
                              letterSpacing: 1, borderRadius: 4 }}>
                  MELHOR ECONOMIA
                </div>
              )}
              <div style={{ background: "#234935", color: "#fff", padding: "6px 10px",
                            borderRadius: 6, marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.8 }}>{s.name}</div>
                <div style={{ fontSize: 9.5, opacity: 0.85 }}>
                  CLT {BRL(s.salario_clt)} · PJ {BRL(s.valor_pj)}
                </div>
              </div>

              {/* PJ bloco */}
              <div style={{ background: "#E8F0FA", padding: 8, borderRadius: 6, marginBottom: 6, border: "1px solid #0F3B6B33" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#0F3B6B", letterSpacing: 1 }}>PJ</div>
                <KV>
                  <Row label="Valor NF" value={BRL(r.pj?.valor_mensal || s.valor_pj)} />
                  <Row label={`Tributos (${PCT(r.pj?.tributos?.aliquota_efetiva_pct || 0)})`} value={BRL(-pjT)} negative dim />
                  <Row label="Contador" value={BRL(-(r.pj?.custo_contador || 0))} negative dim />
                </KV>
                <ResultRowCard color="#0F3B6B" label="Líquido PJ" value={`${BRL(pjLq)}/mês`} />
                <ResultRowCard color="#0F3B6B" label="Custo PJ" value={`${BRL(pjCe)}/mês`} secondary={`${BRL(pjCeA)}/ano`} />
              </div>

              {/* CLT bloco */}
              <div style={{ background: "#E7F0EB", padding: 8, borderRadius: 6, marginBottom: 6, border: "1px solid #23493533" }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#234935", letterSpacing: 1 }}>CLT</div>
                <KV>
                  <Row label="Salário Base" value={BRL(s.salario_clt)} />
                  <Row label="Proventos+Provisões" value={BRL(r.clt?.proventos?.total_proventos_mensal || 0)} dim />
                  <Row label="Encargos Empresa" value={BRL(r.clt?.encargos_empresa?.total || 0)} dim />
                </KV>
                <ResultRowCard color="#234935" label="Líquido Folha" value={`${BRL(cltLq)}/mês`} hint="Mão" />
                <ResultRowCard color="#234935" label="Líquido Econômico" value={`${BRL(cltLqEco)}/mês`} hint="Base comparativa" />
                <ResultRowCard color="#234935" label="Custo CLT" value={`${BRL(cltCe)}/mês`} secondary={`${BRL(cltCeA)}/ano`} />
              </div>

              {/* Comparativo */}
              <div style={{ borderTop: "1px dashed #d7d9d4", paddingTop: 6 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#8A6B2B", letterSpacing: 1, marginBottom: 4 }}>
                  COMPARATIVO
                </div>
                <KV>
                  <Row label="Economia Empresa" value={PCT(eco)} bold />
                  <Row label="Mensal" value={BRL(cmp.custo_empresa?.economia_mensal || 0)} dim />
                  <Row label="Anual" value={BRL(cmp.custo_empresa?.economia_anual || 0)} dim />
                  <Row label="Dif. líquido %" value={PCT(cmp.liquido_colaborador?.diferenca_pct || 0)} />
                  <Row label="Modalidade sup." value={(cmp.liquido_colaborador?.pj_mensal || 0) > (cmp.liquido_colaborador?.clt_mensal || 0) ? "PJ" : "CLT"} />
                </KV>
              </div>
            </div>
          );
        })}
      </div>

      {/* Resumo final */}
      <div style={{ marginTop: 16, border: "2px solid #234935", borderRadius: 10, padding: 12, background: "#F8F6ED" }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#234935", letterSpacing: 1, marginBottom: 8 }}>
          SÍNTESE EXECUTIVA · COMPARATIVO ENTRE CENÁRIOS
        </div>
        <div style={{ fontSize: 11, color: "#1a231f" }}>
          {bestEconomy.i >= 0 ? (
            <>
              O cenário <b style={{ color: "#8A6B2B" }}>{scenarios[bestEconomy.i].name}</b> apresenta
              a maior economia percentual com a modalidade PJ ({PCT(Math.abs(bestEconomy.v))}),
              totalizando aproximadamente {BRL(Math.abs(scenarios[bestEconomy.i].result?.comparativo?.custo_empresa?.economia_anual || 0))} ao ano.
              Os demais cenários aparecem com economia proporcional conforme a estrutura salarial escolhida.
              Para aprofundar a análise individual (composição tributária, descontos CLT detalhados e memória de cálculo),
              consulte o relatório individual de cada cenário no histórico.
            </>
          ) : (
            <>Dados insuficientes para síntese automática.</>
          )}
        </div>
      </div>

      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between",
                    borderTop: "1px solid #d7d9d4", paddingTop: 6, fontSize: 9.5, color: "#8A6B2B" }}>
        <span>THEOKRATOS RH · Relatório multi-cenário gerado automaticamente.</span>
        <span>{now.toLocaleDateString("pt-BR")}</span>
      </div>
    </div>
  );
}

// Reaproveita helpers visuais locais (compactos)
const KV = ({ children }) => <div style={{ display: "flex", flexDirection: "column" }}>{children}</div>;
const Row = ({ label, value, negative, dim, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "1px 0",
                fontSize: dim ? 10 : 10.5,
                color: dim ? "#5a6b63" : "#1a231f",
                fontWeight: bold ? 700 : 400 }}>
    <span>{label}</span>
    <span style={{ fontVariantNumeric: "tabular-nums",
                   color: negative ? "#B91C1C" : (bold ? "#234935" : "inherit") }}>{value}</span>
  </div>
);
const ResultRowCard = ({ color, label, value, secondary, hint }) => (
  <div style={{ marginTop: 4, padding: 5, background: "#fff", borderRadius: 4, border: `1px solid ${color}22` }}>
    <div style={{ fontSize: 9, letterSpacing: 1, color, fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 12, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    {secondary && <div style={{ fontSize: 9.5, color: "#5a6b63", fontVariantNumeric: "tabular-nums" }}>{secondary}</div>}
    {hint && <div style={{ fontSize: 8.5, color: "#8A6B2B", fontStyle: "italic" }}>{hint}</div>}
  </div>
);
const MetaItem = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 8.5, letterSpacing: 1.4, color: "#8A6B2B", fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 11, color: "#1a231f", fontWeight: 600 }}>{value}</div>
  </div>
);
